import React from "react";
import type { IPagingInfo, IPagingFilter } from "@frui.ts/data";
import { handlePageChanged } from "@frui.ts/data";
import Pagination from "react-bootstrap/Pagination";
import { scrollTableTop } from "utils/scrollTop";

export interface PaginatorProps {
  pagingInfo?: IPagingInfo;
  filter?: IPagingFilter;
  onPageChanged: () => void;
  className?: string;
}

const Paginator = (props: PaginatorProps) => {
  if (!props.pagingInfo) {
    return null;
  }

  const { offset, limit, totalItems } = props.pagingInfo;

  if (limit >= totalItems) {
    return null;
  }

  const totalPages = Math.ceil(totalItems / limit);
  const currentPage = Math.ceil(offset / limit) + 1;

  const displayPagesOffset = 2;
  const totalPagesToDisplay = 2 * displayPagesOffset + 1;
  const minPageOffset = Math.max(currentPage + displayPagesOffset - totalPages, 0) + displayPagesOffset;
  const minPage = Math.max(1, currentPage - minPageOffset);

  const pageNumbers = [];
  for (let i = minPage; i <= totalPages && pageNumbers.length < totalPagesToDisplay; i++) {
    pageNumbers.push(i);
  }

  const pageChangedHandler: React.MouseEventHandler<HTMLButtonElement> = e => {
    const pageNumber = +(e.currentTarget.dataset.page ?? 0);
    handlePageChanged(pageNumber, props.filter, () => {
      props.onPageChanged();
      setTimeout(() => {
        scrollTableTop();
      }, 200);
    });
  };

  return (
    <Pagination className={props.className}>
      {pageNumbers[0] !== 1 && <Pagination.First data-page={1} onClick={pageChangedHandler} />}

      {currentPage > 1 && <Pagination.Prev data-page={currentPage - 1} onClick={pageChangedHandler} />}

      {pageNumbers.map(x => (
        <Pagination.Item key={x} data-page={x} active={x === currentPage} onClick={pageChangedHandler}>
          {x}
        </Pagination.Item>
      ))}

      {currentPage < totalPages && <Pagination.Next data-page={currentPage + 1} onClick={pageChangedHandler} />}

      {pageNumbers[pageNumbers.length - 1] !== totalPages && (
        <Pagination.Last data-page={totalPages} onClick={pageChangedHandler} />
      )}
    </Pagination>
  );
};

export default Paginator;
